import { useIntl } from '@capsicum/common';
import { Skeleton, SkeletonItem } from '@capsicum/common-web';
import { type SmsLoginFormType } from '@capsicum/socios-core';
import { Phone } from '@capsicum/socios-core-web';
import { useMemo } from 'react';
import { FormProvider, type UseFormReturn } from 'react-hook-form';
import styled from 'styled-components';

type MobileInputProps = {
	form: UseFormReturn<SmsLoginFormType, unknown, SmsLoginFormType>;
	loading?: boolean;
	readonly?: boolean;
};

export const MobileInput = ({ form, loading, readonly }: MobileInputProps) => {
	const { $t } = useIntl();

	const allErrors = useMemo(() => Object.values(form.formState.errors).map(v => v?.message), [form.formState]);

	return (
		<FormProvider {...form}>
			<S.PhoneNumberTitle>{$t('jurisdictionRequirements.mobileVerification.title')}</S.PhoneNumberTitle>

			{loading ? (
				<Skeleton>
					<S.PhoneNumberLabelSkeleton borderRadius={6} height={21} />
					<SkeletonItem borderRadius={12} height={56} />
				</Skeleton>
			) : (
				<>
					{!readonly && <S.PhoneNumberLabel>{$t('auth.phoneNumberLabel')}</S.PhoneNumberLabel>}
					<Phone disabled={readonly} testID="mobile-verification-input" />
				</>
			)}
			{allErrors?.[0] ? <S.SupportingInputInfo>{allErrors?.[0]}</S.SupportingInputInfo> : null}
		</FormProvider>
	);
};

namespace S {
	export const PhoneNumberTitle = styled.h1`
		${({ theme }) => theme.fonts.headings.title5}
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;

	export const PhoneNumberLabel = styled.p`
		${({ theme }) => theme.fonts.text.text5}
		color: ${({ theme }) => theme.colors.text.main.enabled};
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;

	export const PhoneNumberLabelSkeleton = styled(SkeletonItem)`
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;

	export const SupportingInputInfo = styled.div`
		${({ theme }) => theme.fonts.text.caption2};
		color: ${({ theme }) => theme.colors.borders.error.enabled};
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;
}
